import React, { useState, DragEvent, useEffect, useRef } from "react";
import "./DropFileInput.scss";
import { FieldAttributes, useField, useFormikContext } from "formik";
import SVG from "shared/SVG";
import { SVG_ENUM } from "enums";
import { isDesktop } from "utils";

interface DropFileInputProps extends FieldAttributes<any> {
  label?: string | JSX.Element;
  name: string;
  className?: string;
  accept?: string;
  optional?: boolean;
  multiple?: number;
  maxSize?: number;
  description?: string | JSX.Element;
  showPreview?: boolean;
  existingFile?: { name: string; url: string };
  existingFiles?: { name: string; url: string }[];
  onServerFileRemoved?: (file: { name: string; url: string }) => void;
}

const DropFileInput = ({
  name,
  className = "",
  label,
  optional,
  accept,
  multiple,
  maxSize,
  description,
  showPreview = false,
  existingFile,
  existingFiles,
  onServerFileRemoved,
  ...props
}: DropFileInputProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [initialServerFile, setInitialServerFile] = useState<any | null>(null);
  const [initialServerFiles, setInitialServerFiles] = useState<
    { name: string; url: string }[]
  >([]);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const formik: any = useFormikContext();
  const [, meta] = useField({ ...props, name });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isMultiple = multiple && multiple > 1;

  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const acceptTokens = (acceptValue?: string) =>
    (acceptValue || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

  const acceptHint = () => {
    const tokens = acceptTokens(accept);
    return tokens.length ? tokens.join(", ") : "dozwolonego formatu";
  };

  const isAcceptedByAccept = (file: File) => {
    const tokens = acceptTokens(accept);
    if (!tokens.length) return true;

    const fileName = (file.name || "").toLowerCase();
    const fileType = (file.type || "").toLowerCase();

    // 1) Rozszerzenia: ".pdf"
    for (const t of tokens) {
      if (t.startsWith(".")) {
        if (fileName.endsWith(t)) return true;
      }
    }

    // 2) MIME: "application/pdf" lub wildcard "image/*"
    for (const t of tokens) {
      if (!t.startsWith(".")) {
        if (t.endsWith("/*")) {
          const prefix = t.slice(0, -2);
          if (fileType.startsWith(prefix + "/")) return true;
        } else {
          if (fileType && fileType === t) return true;
        }
      }
    }

    return false;
  };

  const pickAcceptedFiles = (incoming: File[]) => {
    const accepted: File[] = [];
    const rejected: File[] = [];

    for (const f of incoming) {
      (isAcceptedByAccept(f) ? accepted : rejected).push(f);
    }

    return { accepted, rejected };
  };

  const applyIncomingFiles = (incomingFiles: File[]) => {
    const { accepted, rejected } = pickAcceptedFiles(incomingFiles);

    if (accepted.length === 0) {
      setAcceptError(`Przyjmuję tylko pliki: ${acceptHint()}.`);
      clearInput();
      return;
    }

    if (maxSize) {
      const oversized = accepted.filter((f) => f.size > maxSize * 1024 * 1024);
      if (oversized.length > 0) {
        setAcceptError(
          `Plik przekracza dozwolony rozmiar ${maxSize} MB.`,
        );
        clearInput();
        return;
      }
    }

    setAcceptError(
      rejected.length > 0
        ? `Odrzucono część plików. Dozwolone: ${acceptHint()}.`
        : null,
    );

    if (multiple && multiple > 1) {
      const newFiles = [...files, ...accepted].slice(0, multiple);
      setFiles(newFiles);
      return;
    }

    setFiles([accepted[0]]);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      applyIncomingFiles(Array.from(event.target.files));
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      applyIncomingFiles(Array.from(event.dataTransfer.files));
    }
  };

  const handleRemoveFile = (index?: number) => {
    if (multiple && multiple > 1 && typeof index === "number") {
      const newFiles = files.filter((_, i) => i !== index);
      setFiles(newFiles);
      if (newFiles.length === 0) setAcceptError(null);
    } else {
      const removingServerFile = initialServerFile !== null && files.length === 0;
      setFiles([]);
      setInitialServerFile(null);
      setAcceptError(null);
      formik.setFieldValue(name, removingServerFile ? false : null);
    }
    clearInput();
  };

  const handleRemoveServerFile = (index: number) => {
    const removed = initialServerFiles[index];
    setInitialServerFiles((prev) => prev.filter((_, i) => i !== index));
    if (removed && onServerFileRemoved) {
      onServerFileRemoved(removed);
    }
  };

  useEffect(() => {
    if (multiple && multiple > 1) {
      formik.setFieldValue(name, files);
    } else {
      if (files.length > 0) {
        formik.setFieldValue(name, files[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEffect(() => {
    if (!formik.values[name]) {
      setFiles([]);
      setInitialServerFile(null);
      setAcceptError(null);
      clearInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values[name]]);

  useEffect(() => {
    if (!isMultiple && existingFile) {
      setInitialServerFile(existingFile);
    } else if (!isMultiple && formik.values[name]) {
      const value = formik.values[name];
      if (value instanceof File) return;
      setInitialServerFile(value);
    }
    if (isMultiple && existingFiles && existingFiles.length > 0) {
      setInitialServerFiles(existingFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canAddMore =
    !isMultiple || files.length + initialServerFiles.length < multiple;
  const allItems: Array<
    | { type: "server"; name: string; url: string }
    | { type: "local"; file: File; index: number }
  > = [
    ...initialServerFiles.map((f) => ({
      type: "server" as const,
      name: f.name,
      url: f.url,
    })),
    ...files.map((f, i) => ({ type: "local" as const, file: f, index: i })),
  ];
  const visibleItems = showAllFiles ? allItems : allItems.slice(0, 5);
  const remainingFilesCount = allItems.length - 5;

  const getServerFileName = (file: any) => {
    if (!file) return "";
    if (file.name) return file.name;
    if (file.filename) return file.filename;
    if (file.originalName) return file.originalName;
    if (typeof file === "string") {
      const parts = file.split("/");
      return parts[parts.length - 1] || file;
    }
    return "Plik";
  };

  return (
    <div
      className={`drop-file-input__wrapper input__wrapper ${className || ""}`}
    >
      <label className=" drop-file-input__container">
        <div className="input__label">
          {label} {optional && <p className="input__optional"> (opcjonalne)</p>}
        </div>
        {canAddMore && (
          <div
            className="drop-file-input__dropzone"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              onChange={handleFileSelect}
              className="drop-file-input__input"
              accept={accept}
              multiple={!!isMultiple}
            />
            <div className="drop-file-input__content">
              <SVG type={SVG_ENUM.CLOUD_UPLOAD} />
              {isDesktop() ? (
                <p>
                  Upuść plik{isMultiple ? "i" : ""} lub{" "}
                  <strong>pobierz z dysku</strong>
                </p>
              ) : (
                <p>
                  <strong>Pobierz z dysku</strong>
                </p>
              )}
            </div>
          </div>
        )}
        {(meta.touched && meta.error) || acceptError ? (
          <div className="input__error drop-file-input__error">
            <span>{(meta.touched && meta.error) || acceptError}</span>
          </div>
        ) : null}
      </label>

      {description && (
        <div className="drop-file-input__description">{description}</div>
      )}

      {isMultiple && allItems.length > 0 && (
        <>
          {allItems.length > 1 && (
            <div className="drop-file-input__attached-header">
              <h4>Załączone pliki ({allItems.length})</h4>
            </div>
          )}
          <div className="drop-file-input__files">
            {visibleItems.map((item, idx) => {
              if (item.type === "server") {
                return (
                  <p key={`server-${idx}`} className="drop-file-input__file">
                    {showPreview && item.url && (
                      <img
                        src={item.url}
                        alt={item.name}
                        className="drop-file-input__preview-thumb"
                      />
                    )}
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="drop-file-input__file-name drop-file-input__file-link"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <span className="drop-file-input__file-name">
                        {item.name}
                      </span>
                    )}
                    <span
                      onClick={() =>
                        handleRemoveServerFile(
                          initialServerFiles.findIndex(
                            (f) => f.name === item.name && f.url === item.url,
                          ),
                        )
                      }
                    >
                      <SVG type={SVG_ENUM.CLOSE_CIRCLE_GRAY} />
                    </span>
                  </p>
                );
              }
              const isImage = item.file.type.startsWith("image/");
              const previewUrl =
                showPreview && isImage ? URL.createObjectURL(item.file) : null;
              return (
                <p
                  key={`local-${item.index}`}
                  className="drop-file-input__file"
                >
                  {showPreview && previewUrl && (
                    <img
                      src={previewUrl}
                      alt={item.file.name}
                      className="drop-file-input__preview-thumb"
                    />
                  )}
                  <span className="drop-file-input__file-name">
                    {item.file.name}
                  </span>
                  <span onClick={() => handleRemoveFile(item.index)}>
                    <SVG type={SVG_ENUM.CLOSE_CIRCLE_GRAY} />
                  </span>
                </p>
              );
            })}
          </div>
          {remainingFilesCount > 0 && !showAllFiles && (
            <div
              className="drop-file-input__show-more"
              onClick={() => setShowAllFiles(true)}
            >
              Wyświetl {remainingFilesCount === 1 ? "pozostały" : "pozostałe"} +{" "}
              {remainingFilesCount}{" "}
              {remainingFilesCount === 1 ? "plik" : "pliki"}
            </div>
          )}
        </>
      )}

      {!isMultiple && files.length > 0 && (
        <>
          <div className="drop-file-input__files">
            {files.map((file, index) => {
              const isImage = file.type.startsWith("image/");
              const previewUrl =
                showPreview && isImage ? URL.createObjectURL(file) : null;

              return (
                <p
                  key={`${file.name}-${index}`}
                  className="drop-file-input__file"
                >
                  {showPreview && previewUrl && (
                    <img
                      src={previewUrl}
                      alt={file.name}
                      className="drop-file-input__preview-thumb"
                    />
                  )}
                  <span className="drop-file-input__file-name">
                    {file.name}
                  </span>
                  <span onClick={() => handleRemoveFile(undefined)}>
                    <SVG type={SVG_ENUM.CLOSE_CIRCLE_GRAY} />
                  </span>
                </p>
              );
            })}
          </div>
        </>
      )}

      {files.length === 0 && !isMultiple && initialServerFile && (
        <div className="drop-file-input__files">
          <p className="drop-file-input__file">
            {initialServerFile.url ? (
              <a
                href={initialServerFile.url}
                target="_blank"
                rel="noreferrer"
                className="drop-file-input__file-link"
              >
                {getServerFileName(initialServerFile)}
              </a>
            ) : (
              <span>{getServerFileName(initialServerFile)}</span>
            )}
            <span onClick={() => handleRemoveFile()}>
              <SVG type={SVG_ENUM.CLOSE_CIRCLE_GRAY} />
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default DropFileInput;
